import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { ShareWizardProvider } from '@/contexts/ShareWizardContext';
import { useAppConfig } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { anySurfaceResumes, resumePathOnSurface } from './navigation_logic';
import ShareFlowShell from './ShareFlowShell';
import GardenShareRouter from './GardenShareRouter';
import ShareWizardRouter from './ShareWizardRouter';

/**
 * Share route for a topic: which surface renders the sharing session.
 *
 * With the garden flag on, the session is the three garden destinations; with
 * it off (the default), it is exactly the classic wizard it has always been.
 * The provider sits above the branch, so the session itself — who, what, how
 * it is worded, what is queued — is the same object either way.
 *
 * The shell sits above the branch too, and the branch is held until the app
 * config has arrived, so that exactly one surface ever mounts. Both matter on
 * a cold load straight into a share URL, where the flag lands a render after
 * the page does:
 *
 * - The shell is this route's `IonPage`. Choosing the surface around it would
 *   swap that page for a second one at the same pathname, which is the case
 *   StackManager cancels — leaving the new page stuck at `ion-page-invisible`
 *   (see `useRecoverCancelledEnterTransition`).
 * - Choosing it inside the shell but without waiting would still mount the
 *   wizard's step at a shared URL like `/share/steps/recipients` and then
 *   replace it with the garden's destination, swapping one `IonPage` for
 *   another at an unchanged path inside a live outlet.
 *
 * Waiting costs a frame of an empty outlet inside an already-transitioning
 * page, and nothing at all on a warm navigation, where the config is long
 * since loaded and the outlet mounts with the shell as before.
 *
 * A URL the chosen surface has no destination for — a wizard step under the
 * garden flag, a garden destination without it — is resumed here, above the
 * shell, and the shell is kept off screen until it has been. It cannot be a
 * fallback route inside the outlet: redirecting replaces one history entry
 * with another, and StackManager unmounts the leaving view on a replace
 * without checking whether it is also the entering one. This route is not
 * exact, so every step URL under it is one view — replacing a step URL from
 * inside takes the whole page down. Sending the sender on before the shell
 * mounts means the replace happens while that view has no `IonPage`
 * registered, which is the one shape StackManager leaves alone.
 */
const ShareFlowGate: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const { features, isLoaded } = useAppConfig();

  /**
   * The URL the flow was entered on. Read once rather than subscribed to: this
   * gate owns the page for every destination under it, so re-rendering it on
   * each move between them would put the shell back through reconciliation for
   * no gain. Only the entry URL can name a destination this surface lacks —
   * from there on the flow only ever navigates to its own.
   */
  const [entry] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));
  const [resumed, setResumed] = useState(false);

  const surface = topicGardenEnabled(features) ? 'garden' : 'wizard';
  const resumeAt =
    isLoaded && !resumed
      ? resumePathOnSurface(surface, topicId, entry.pathname, entry.search)
      : null;

  // The redirect above has already replaced the URL by the time this runs; the
  // flag drops the gate back to rendering the flow, now at a path it has a
  // destination for.
  useEffect(() => {
    if (resumeAt !== null) setResumed(true);
  }, [resumeAt]);

  if (!resumed && anySurfaceResumes(entry.pathname)) {
    if (!isLoaded) return null;
    if (resumeAt !== null) return <Redirect to={resumeAt} />;
  }

  return (
    <ShareWizardProvider topicId={topicId}>
      <ShareFlowShell>
        {isLoaded &&
          (surface === 'garden' ? (
            <GardenShareRouter />
          ) : (
            <ShareWizardRouter />
          ))}
      </ShareFlowShell>
    </ShareWizardProvider>
  );
};

export default ShareFlowGate;
